import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import ApiHelper from "src/services/ApiHelper.service";
import {
  firebaseEvent,
  firebaseSetUserId,
} from "src/services/analytics/firebase";

type Login = {
  loginText: string;
  errorText: string;
  token: string;
  profile: Profile;
  setTokenAndProfile: (e: any) => Promise<void>;
  logout: () => Promise<void>;
  reset: () => void;
};

type Profile = {
  email: string;
  name: string;
  id: string;
  permissions: string[];
  photo: string;
  role: unknown;
};

const initialState = {
  loginText: "",
  errorText: "",
  token: "",
  profile: {
    email: "",
    name: "",
    id: "",
    permissions: [],
    photo: "",
    role: null,
  },
};

export const useAuthStore = create<Login>()(
  persist(
    (set, get) => ({
      ...initialState,
      setTokenAndProfile: async (callbackData) => {
        set({ loginText: "Logging in..." });

        try {
          const { token } = await ApiHelper.post("/auth", callbackData, {
            headers: {
              Authorization: "placeholder",
              "Content-Type": "application/json",
            },
          });

          if (token) {
            set({ token, loginText: "Retrieving profile and access..." });

            const profile = await ApiHelper.get("/user/me");
            firebaseSetUserId(profile.id);
            firebaseEvent("login", { id: profile.id });
            set({ profile, loginText: "", errorText: "" });
          }
        } catch (err) {
          set({
            errorText: "Failed to communicate with the server",
            loginText: "",
          });
          console.error(err);
          throw err;
        }
      },
      logout: async (): Promise<void> => {
        const token = get().token;
        if (token) {
          // If token exist, invalidate on the server-side
          await ApiHelper.delete(
            "/auth",
            {},
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );
          set(initialState);
        }
      },
      reset: () => {
        set(initialState);
      },
    }),
    {
      name: "_auth",
      storage: createJSONStorage(() => window.sessionStorage),
    }
  )
);

// To refetch user/me when user refresh page
export const refetchProfileOnRefresh = async () => {
  const perfEntries = performance.getEntriesByType("navigation");
  // Check if page is accessed by reload
  // If performance.getEntriesByType is not supported by browser,
  // user have to relogin to get their profile/access updated
  if (perfEntries && (perfEntries[0] as any).type === "reload") {
    const { state } = JSON.parse(sessionStorage.getItem("_auth") || "{}");
    if (state.token) {
      const profile = await ApiHelper.get("/user/me");
      sessionStorage.setItem(
        "_auth",
        JSON.stringify({ state: { ...state, profile } })
      );
    }
  }
};
